import { MonetaryAmount } from "../../../../../modules/currency/currencyTypes"
import { OfferDetailsType, ProductStockInfo } from "../../../../../modules/merchant/admin/products/productsTypes"
import { ProductAvailability } from "../../../../types/productAvailabilityTypes"

export const FAVORITES_GROUP_ID = -1

export type TakeawayKitchenWebshop = Readonly<{
    id?: number
    uid?: string
    name?: string
    orderType?: string
    type?: string
    demoMode?: boolean
}>

export type TakeawayKitchenBrandingDetails = Readonly<{
    imageUrl?: string
    description?: string
    chefName?: string
    website?: string
    openingHours?: string
}>

export type TakeawayKitchen = Readonly<{
    id: number
    name: string
    phoneNumber?: string
    email?: string
    streetName?: string
    streetNumber?: string
    postalCode?: string
    city?: string
    kitchenType?: string
    webshop?: TakeawayKitchenWebshop
    brandingDetails?: TakeawayKitchenBrandingDetails
    deadline?: Readonly<{
        id: number
        acceptWeekendSale?: boolean
    }>
}>

export type TakeawayUserDetails = Readonly<{
    favoriteType?: string
    favoriteId?: number
    canFavorite?: boolean
}>

export type TakeawayProductDescription = Readonly<{
    marketingTag?: string
}>

export type TakeawayProductDeliveryDetails = Readonly<{
    pickupLocation?: string
    pickUpFromTime?: string
}>

export type TakeawayProductOrderDetails = Readonly<{
    availability?: ProductAvailability
    orderToGuest?: boolean
    productLeftInStock?: number
    itemsForSale?: number
    itemsInstock?: number
    productsOnSale?: number
    deliveryDetails?: TakeawayProductDeliveryDetails
}>

export type TakeawayCardDetails = Readonly<{
    imageUrl?: string
    cardColor?: string
    tag?: string
    tagColor?: string
}>

export type TakeawayMarketingDetails = Readonly<{
    isOffer?: boolean
}>

export type TakeawayProductKitchen = Readonly<{
    id: number
    name: string
    streetName?: string
    streetNumber?: string
    postalCode?: string
    city?: string
}>

export type TakeawayProduct = Readonly<{
    id: number
    name: string
    description?: string
    price?: MonetaryAmount
    imageUrl?: string
    offerDetails?: OfferDetailsType
    stock?: ProductStockInfo
    orderDetails?: TakeawayProductOrderDetails
    userDetails?: TakeawayUserDetails
    tags?: string[]
    productDescription?: TakeawayProductDescription
    headerTag?: string
    headerTagColor?: string
    cardDetails?: TakeawayCardDetails
    marketingDetails?: TakeawayMarketingDetails
    productType?: string
    paymentDetails?: Readonly<{
        method?: string
    }>
    kitchen?: TakeawayProductKitchen
}>

export type TakeawayProductGroup = Readonly<{
    id: number
    name: string
    products: TakeawayProduct[]
}>

export type TakeawayMenu = Readonly<{
    date: string
    displayDate: string
    productGroups: TakeawayProductGroup[]
}>

export type TakeawayMenuResponse = Readonly<{
    messages?: string[]
    kitchen?: TakeawayKitchen
    menues: TakeawayMenu[]
}>

export type TakeawayKitchensPagination = Readonly<{
    nextLink?: {
        rel: string
        href: string
    }
}>

export type TakeawayKitchensResponse = Readonly<{
    kitchens: TakeawayKitchen[]
    pagination?: TakeawayKitchensPagination
}>

export type TakeawayCartItem = Readonly<{
    product: TakeawayProduct
    quantity: number
}>

export type TakeawayProductFavoritesResponse = Readonly<{
    products: TakeawayProduct[]
    pagination?: TakeawayKitchensPagination
}>

export type TakeawayPaymentCard = Readonly<{
    id: number
    type?: string
    number?: string
    displayName?: string
    hasExpired?: boolean
    expiryDate?: string
}>

export type TakeawayPaymentMethodIcon = Readonly<
    { type: "IMAGE_URL"; url: string } | { type: "FONT_AWESOME"; spec?: string; color?: string }
>

export type TakeawayAccountingDimension = Readonly<{
    id: number
    inputField: Readonly<{ name: string; label?: string }>
}>

export type TakeawayPaymentMethod = Readonly<{
    value: string
    translationKey?: string
    showSalesConditions?: boolean
    card?: TakeawayPaymentCard
    icon?: TakeawayPaymentMethodIcon
    notice?: string
    accounting?: Readonly<{
        dimensions?: TakeawayAccountingDimension[]
    }>
}>

export type TakeawayPaymentDetailsResponse = Readonly<{
    paymentMethods: TakeawayPaymentMethod[]
    salesConditionsUrl?: string
    currencyCodes?: string[]
    userAccountBalance?: MonetaryAmount
    deliveryDetails?: Readonly<{ fillGuestDetails: boolean }>
}>

export type TakeawayOrderLine = Readonly<{
    productId: number
    items: number
    buyerParty: string
}>

export enum TakeawayDeliveryLocationType {
    DELIVERY_LOCATION = "DELIVERY_LOCATION",
}

export type TakeawayDeliveryLocationRef = Readonly<
    { type: TakeawayDeliveryLocationType.DELIVERY_LOCATION; id: number } | { name: string }
>

export type TakeawayOrderDelivery = Readonly<{
    deliveryType: string
    deliveryLocation: TakeawayDeliveryLocationRef
    deliveryTime: string
    orderLines: TakeawayOrderLine[]
}>

export type TakeawayOrderOrganizer = Readonly<{
    name: string
    email: string
    mobilePhone: string
    organizerType: string
}>

export type TakeawayDeliveryLocation = Readonly<{
    id: number
    displayName: string
    description?: string
    timeInterval?: Readonly<{
        start: string
        end: string
    }>
}>

export type TakeawayDeliveryLocationsResponse = Readonly<{
    deliveryLocations: TakeawayDeliveryLocation[]
}>

export type TakeawayOrderCustomer = Readonly<{
    email?: string
    phoneNumber?: string
}>

export type CreateTakeawayOrderRequest = Readonly<{
    kitchen: Readonly<{ id: number }>
    webshop: Readonly<{ uid: string }>
    payment: Readonly<{
        method: string
        card?: Readonly<{ id: number }>
        accounting?: Readonly<{
            dimensions: ReadonlyArray<Readonly<{ id: number; value: string }>>
        }>
    }>
    orderNote: string
    deliveries: TakeawayOrderDelivery[]
    organizers?: ReadonlyArray<TakeawayOrderOrganizer>
    customer?: TakeawayOrderCustomer
    onBehalfOf?: Readonly<{ userId: number }>
}>
